import React, { useCallback, useEffect, useState } from "react";
import { useLang } from "../context/LanguageContext";
import { PageHeader } from "../components/common/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { ConnectionCard } from "../components/whatsapp/ConnectionCard";
import { SettingsPanel } from "../components/whatsapp/SettingsPanel";
import { TemplatesPanel } from "../components/whatsapp/TemplatesPanel";
import { QueueBuilder } from "../components/whatsapp/QueueBuilder";
import { ReminderTable } from "../components/whatsapp/ReminderTable";
import { CLFs, VOs, SHGs, Members, Reminders } from "../lib/api";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const WhatsAppPage = () => {
  const { t } = useLang();
  const [status, setStatus] = useState(null);
  const [s, setS] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [refs, setRefs] = useState({ clfs: [], vos: [], shgs: [], members: [] });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);

  const load = useCallback(async () => {
    try {
      const [st, set, clfs, vos, shgs, members] = await Promise.all([Reminders.status(), Reminders.settings(), CLFs.list(), VOs.list(), SHGs.list(), Members.list()]);
      const { placeholders: ph, updated_at, ...rest } = set;
      setStatus(st); setS(rest); setPlaceholders(ph); setRefs({ clfs, vos, shgs, members });
    } catch { toast.error("Could not load WhatsApp reminder data"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try { const { placeholders: _p, updated_at, ...rest } = await Reminders.saveSettings(s); setS(rest); toast.success("Reminder settings saved"); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const sendAll = async () => {
    setSending(true);
    try { const r = await Reminders.sendQueued(); toast.success(`Submitted ${r.submitted}, failed ${r.failed}, skipped ${r.skipped}. Delivery is confirmed by WhatsApp later.`); bump(); }
    catch (e) { toast.error(e.message); } finally { setSending(false); }
  };

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader title={t.whatsapp.title} subtitle="Prepare EMI reminders for members who have given WhatsApp consent" testId="whatsapp-page-header" showSampleBadge={false} />
      <ConnectionCard status={status} />
      {!s ? <div className="rounded-2xl border border-emerald-950/10 bg-white p-10 text-center text-sm text-emerald-900/50"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading…</div> : (
        <Tabs defaultValue="queue">
          <TabsList className="mb-4 h-auto flex-wrap justify-start bg-emerald-900/5" data-testid="whatsapp-tabs">
            <TabsTrigger value="queue" data-testid="tab-queue">Queue</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Message history</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          </TabsList>
          <TabsContent value="queue" className="space-y-4">
            <QueueBuilder refs={refs} onQueued={bump} />
            <div className="flex items-center justify-between">
              <h3 className="font-display text-base font-semibold text-emerald-950">Waiting to send</h3>
              <Button onClick={sendAll} disabled={!status?.configured || sending} title={status?.configured ? "" : "WhatsApp not connected"}
                className="bg-[#25D366] text-white hover:bg-[#1EB354]" data-testid="send-all-queued-btn">
                {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
                {status?.configured ? "Send all queued" : "Sending disabled — not connected"}
              </Button>
            </div>
            <ReminderTable initialStatus="Queued" refreshKey={refreshKey} configured={status?.configured} maxAttempts={s.max_attempts} onChanged={bump} testId="queue-table" />
          </TabsContent>
          <TabsContent value="history">
            <ReminderTable refreshKey={refreshKey} configured={status?.configured} maxAttempts={s.max_attempts} onChanged={bump} testId="history-table" />
          </TabsContent>
          <TabsContent value="settings"><SettingsPanel s={s} setS={setS} onSave={save} saving={saving} /></TabsContent>
          <TabsContent value="templates"><TemplatesPanel s={s} setS={setS} placeholders={placeholders} onSave={save} saving={saving} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default WhatsAppPage;
